import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL, getToken, clearSession, getUser, getRefreshToken } from '../constants';
import { reauthBus } from '../../utils/reauthBus';
import { renouvelerSilencieusement } from '../../services/renouvellementSession';
import { installerSurAxios } from '../../utils/moniteurReseau';
import i18n from '../../i18n';

// withCredentials : le cookie `cb_did` identifie l'appareil et doit accompagner
// les requêtes vers l'API, servie sur une origine distincte du front. Le CORS du
// backend autorise déjà les identifiants (`setAllowCredentials(true)`) sur une
// liste d'origines explicite.
const axiosInstance = axios.create({ baseURL: API_URL, withCredentials: true });

// Detection des requetes lentes. Pose AVANT les intercepteurs ci-dessous, et
// l'ordre compte : les intercepteurs de reponse se declenchent dans l'ordre de
// pose, donc la requete est liberee des l'erreur. Sinon un 401 partirait dans
// `reauthBus.openAndQueue`, dont la promesse reste en attente tant que
// l'utilisateur n'a pas ressaisi son mot de passe - et le bandeau resterait
// affiche pendant tout ce temps.
installerSurAxios(axiosInstance);
// `authService`, `renouvellementSession` et `reauthBus` appellent axios
// directement pour echapper a ces intercepteurs ; l'instance par defaut est
// donc equipee a son tour, faute de quoi l'ecran de connexion - la ou une
// connexion lente se ressent le plus - ne dirait rien.
installerSurAxios(axios);

// Endpoints d'authentification publique : un 401 ici NE déclenche PAS
// le popup (mauvais mdp lors d'un login, par exemple → toast classique).
const PUBLIC_AUTH_PATHS = [
  '/v1/auth/login',
  '/v1/auth/refresh-token',
  '/v1/auth/renouveler-token',
  '/v1/auth/verify-otp',
  '/v1/auth/resend-otp',
  '/v1/auth/activation',
  '/v1/auth/forgot-password',
  '/v1/auth/reset-password',
  '/v1/auth/appareil/demander-validation',
];

const isPublicAuthEndpoint = (url = '') =>
  PUBLIC_AUTH_PATHS.some((p) => url.includes(p));

/* Langue de l'écran, pour que le serveur traduise ses messages (erreurs,
   confirmations) quand l'agent a choisi l'anglais. Explicite et toujours envoyée :
   la langue du navigateur (Accept-Language) ne doit rien décider, un poste en
   anglais avec l'écran en français garde ses messages en français. */
export const enteteLangue = () => (i18n.language === 'en' ? 'en' : 'fr');

axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Langue-Interface'] = enteteLangue();
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  if (!isFormData && !config.headers['Content-Type'])
    config.headers['Content-Type'] = 'application/json';
  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url ?? '';
    const msg = err?.response?.data?.message || err?.response?.data?.error;

    if (status === 401) {
      // 401 sur endpoint d'auth public OU pas d'utilisateur connu → fallback redirect
      if (isPublicAuthEndpoint(url) || !getUser()) {
        // clearSession et non clearToken : on renvoie vers la connexion, mais on
        // garde le « Se souvenir de moi ». Si le jeton est reellement mort, le
        // renouvellement au prochain chargement le constatera et le jettera lui-meme.
        clearSession();
        window.location.href = '/login';
        return Promise.reject(err);
      }

      // « Se souvenir de moi » : tenter un renouvellement silencieux AVANT de
      // demander quoi que ce soit. Le drapeau evite la boucle - si la requete
      // rejouee echoue encore en 401, on passe a la fenetre de re-saisie.
      if (getRefreshToken() && !err.config?._renouvellementTente) {
        err.config._renouvellementTente = true;
        return renouvelerSilencieusement().then((renouvele) => {
          if (!renouvele) return reauthBus.openAndQueue(err.config);
          // L'intercepteur de requete réinjectera le nouveau jeton.
          return axiosInstance(err.config);
        });
      }

      // 401 sur endpoint protégé → popup de re-auth, requête mise en queue puis rejouée
      return reauthBus.openAndQueue(err.config);
    }

    const skipToast = err?.config?.skipErrorToast;

    /* SERVICE ABSENT, PAS ACTION REFUSÉE. 502, 503 et 504 ne disent pas « votre
       saisie est mauvaise » mais « le serveur applicatif ne répond plus ». Traités
       comme les autres erreurs, ils affichaient un message rouge que l'utilisateur
       attribuait à sa propre action, et le poussaient à réessayer en boucle. On
       signale l'état à l'application, qui affiche un écran de maintenance ; le toast
       est supprimé, l'écran dit déjà tout.

       `status` est indéfini quand la requête n'a pas abouti du tout (serveur
       injoignable, DNS, coupure réseau). Ce cas n'est PAS traité ici : il se
       confond avec une perte de connexion côté client, dont le bandeau de
       connexion lente a déjà la charge. */
    if (status === 502 || status === 503 || status === 504) {
      window.dispatchEvent(new CustomEvent('cb:service-indisponible'));
      return Promise.reject(err);
    }

    if (status === 403) {
      if (!skipToast) toast.error(msg || i18n.t('referentiels:api.accesNonAutorise'));
    } else if (status === 404) {
      // silencieux
    } else if (msg && !skipToast) {
      toast.error(msg);
    }
    return Promise.reject(err);
  }
);

export default axiosInstance;
